import React from 'react';
import cn from 'classnames';
import {t} from '@lingui/core/macro';

import Edit from 'shared/ui/icons/General/Edit';
import OpenLinkNewWindow from 'shared/ui/IconButton/OpenLinkNewWindow/OpenLinkNewWindow';
import {ExceededModal} from 'entities/user';
import StatusToggle from 'shared/ui/StatusToggle/StatusToggle';
import RowTags from 'shared/ui/Table/RowTags/RowTags';
import IHttpMonitorOutput from 'features/http/model/HttpMonitor/IHttpMonitor';
import useHttpMonitorRow from 'features/http/model/useHttpMonitorRow';
import {isTranslate} from 'shared/lib/lang';

import {MonitorMenu, MonitorRowStatusCell, MonitorTagsModal} from 'entities/monitor';


import useUpdateHttpMonitorTags from 'features/http/model/useUpdateHttpMonitorTags';


import HttpMonitorPauseModal from '../HttpMonitorPauseModal/HttpMonitorPauseModal';
import HttpMonitorResumeModal from '../HttpMonitorResumeModal/HttpMonitorResumeModal';
import HttpMonitorDeleteModal from '../HttpMonitorDeleteModal/HttpMonitorDeleteModal';
import HttpMonitorModal from '../HttpMonitorModal';

import HttpMonitorLocationsCounterStatus from '../HttpMonitorLocationsCounterStatus/HttpMonitorLocationsCounterStatus';

import styles from './HttpMonitorRow.module.scss';

interface IHttpMonitorRowProps {
    item: IHttpMonitorOutput,
    hasSelectedItems: boolean,
    isMassOperationsMode: boolean,
}

const HttpMonitorRow: React.FC<IHttpMonitorRowProps> = ({item, hasSelectedItems, isMassOperationsMode}) => {
    const {
        activeModal,
        closeModal,
        canCreate,
        isSelected,
        isPaused,
        tags,
        displayName,
        onCheckboxChange,
        onTagClick,
        onIncidentsClick,
        onLogClick,
        onEditClick,
        onPauseClick,
        onResumeClick,
        onDeleteClick,
        onTagsClick,
        onToggleClick,
        onCheckNow,
    } = useHttpMonitorRow(item);

    return (
        <>
            <tr
                className={cn(
                    styles.row,
                    isPaused && styles.isPaused,
                )}
                onClick={onIncidentsClick}
            >
                <MonitorRowStatusCell
                    isMassOperationsMode={isMassOperationsMode}
                    hasSelectedItems={hasSelectedItems}
                    isSelected={isSelected}
                    onCheckboxChange={onCheckboxChange}
                    isPaused={isPaused}
                    ok={item.ok}
                    incompleteResult={item.incomplete_result}
                />
                <td className={styles.blockName}>
                    <div className={styles.containerName}>
                        <span className={styles.name} title={displayName}>
                            {displayName}
                        </span>
                        <span className={styles.editIcon} onClick={onEditClick}>
                            <Edit/>
                        </span>
                    </div>
                    <div
                        className={styles.openLinkWrapper}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <OpenLinkNewWindow
                            url={item.url_human_readable}
                            className={styles.openLinkIcon}
                            styleText={styles.url}
                        />
                    </div>
                    {tags.length > 0 && (
                        <RowTags tags={tags} onTagClick={onTagClick} disabled={hasSelectedItems}/>
                    )}
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    <HttpMonitorLocationsCounterStatus
                        title={t`Последний результат`}
                        count={item.success_locations_count ?? 0}
                        totalLocationsCount={item.total_locations_count ?? 0}
                        lastResult={item.last_results}
                        ok={item.ok}
                    />
                </td>
                <td>
                    {!isPaused
                        ? <div>{item.availability1}%</div>
                        : <div>{t`На паузе`}</div>
                    }
                </td>
                <td>{item.check_interval} {t`мин.`}</td>
                <td className={styles.toggleCell}>
                    <StatusToggle
                        condition={item.status === 'active'}
                        onClick={onToggleClick}
                        notActiveText={t`На паузе`}
                        preventToggle={isPaused && !canCreate}
                    />
                </td>
                <td onClick={(e) => e.stopPropagation()}>
                    <div className="d-flex justify-content-center">
                        <MonitorMenu
                            isActive={item.status === 'active'}
                            showToggle={item.status !== 'unsubscribed'}
                            onCheckClick={onCheckNow}
                            onPauseClick={onPauseClick}
                            onResumeClick={onResumeClick}
                            onEditClick={onEditClick}
                            onTagsClick={onTagsClick}
                            onDetailsClick={onIncidentsClick}
                            onLogClick={onLogClick}
                            onDeleteClick={onDeleteClick}
                        />
                    </div>
                </td>
            </tr>

            <HttpMonitorModal
                item={item}
                open={activeModal === 'edit'}
                handleClose={closeModal}
            />

            <HttpMonitorPauseModal
                item={item}
                open={activeModal === 'pause'}
                handleClose={closeModal}
            />

            <HttpMonitorResumeModal
                item={item}
                open={activeModal === 'resume'}
                handleClose={closeModal}
            />

            <HttpMonitorDeleteModal
                item={item}
                open={activeModal === 'delete'}
                handleClose={closeModal}
            />

            <MonitorTagsModal
                uuid={item.uuid}
                tags={item.tags}
                useUpdateTags={useUpdateHttpMonitorTags}
                dialogClassName={isTranslate() ? styles.tagsEnBackground : styles.tagsBackground}
                open={activeModal === 'tags'}
                handleClose={closeModal}
            />

            <ExceededModal
                isOpen={activeModal === 'limit'}
                handleClose={closeModal}
            />
        </>
    );
};

export default HttpMonitorRow;